import { inject, Injectable } from '@angular/core';
import { FundraiserMinimal } from '@fmnts/api';
import { isDefined } from '@fmnts/core';
import {
  AbstractGraphNodeBuilder,
  DeclarativeFieldComponent,
  FieldNodeInputDefinitionList,
  GraphBuilderContext,
} from '@fmnts/declarative-forms';
import {
  FieldOperatorName,
  FormOperatorName,
  RxJsOperatorNodeName,
} from '@fmnts/declarative-forms/common';
import { LegacyStore } from '@formunauts/next/data-access';
import { filter, map } from 'rxjs';

export enum FundraiserNodeName {
  CurrentFundraiser = 'fundraiser.current$',
}

const fakeFundraiserDefinitions: FieldNodeInputDefinitionList = {
  value: {
    name: FundraiserNodeName.CurrentFundraiser,
    pipe: [
      {
        operator: RxJsOperatorNodeName.Pluck,
        args: ['id'],
      },
      {
        operator: FieldOperatorName.FilterValueInItems,
      },
      {
        operator: FormOperatorName.FieldUseLatestAsDefaultValue,
      },
    ],
  },
};

/**
 * Builder for fundraiser related graph nodes
 */
@Injectable({
  providedIn: 'root',
})
export class FundraiserGraphNodeBuilder extends AbstractGraphNodeBuilder<
  FundraiserNodeName,
  string
> {
  private readonly store = inject(LegacyStore);

  constructor() {
    super();

    // Provide source
    this.addNode(FundraiserNodeName.CurrentFundraiser, () =>
      this.currentFundraiser$(),
    );
  }

  /**
   * @returns
   * An observable that emits the current fundraiser
   */
  private currentFundraiser$() {
    return this.store.select<FundraiserMinimal>('next.fundraisers.self').pipe(
      // Since the user is logged in, it should always be defined
      filter(isDefined),
      // TODO: Remove once FundraiserMinimal uses number again as ID and
      // we can safely assume that no more string IDs are emitted from storage
      map((fundraiser) => ({
        ...fundraiser,
        id: Number(fundraiser.id),
      })),
    );
  }

  createFakeDefinitions(ctx: GraphBuilderContext) {
    const genericComponent = ctx.injector.get(DeclarativeFieldComponent, null);

    switch (genericComponent?.field?.key) {
      case 'fundraiser':
        return fakeFundraiserDefinitions;
    }
  }
}
